import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Freight, PaginatedFreights } from "@/types/freight";
import type { FreightFormData } from "@/validators/freight";

export interface ListFreightParams {
  search?: string;
  status?: string;
  paymentStatus?: "paid" | "unpaid";
  companyId?: string;
  driverId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  perPage?: number;
}

export interface ReceiveFreightData {
  paymentDate: string;
  method: "PIX" | "BANK_TRANSFER" | "BANK_SLIP" | "CASH" | "OTHER";
  accountId?: string;
  notes?: string;
}

const KEYS = {
  list: (params: ListFreightParams) => ["freights", params] as const,
  detail: (id: string) => ["freights", id] as const,
};

export function useFreights(params: ListFreightParams = {}) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () =>
      api.get<PaginatedFreights>("/freights", { params }).then((r) => r.data),
  });
}

export function useFreight(id: string) {
  return useQuery({
    queryKey: KEYS.detail(id),
    queryFn: () => api.get<Freight>(`/freights/${id}`).then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreateFreight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: FreightFormData) =>
      api.post<Freight>("/freights", data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["freights"] }),
  });
}

export function useUpdateFreight(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: FreightFormData) =>
      api.put<Freight>(`/freights/${id}`, data).then((r) => r.data),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["freights"] });
      qc.setQueryData(KEYS.detail(id), updated);
    },
  });
}

export interface AssignFreightData {
  driverId: string;
  vehicleId: string;
  driverValue: number;
  advancePercent: number;
}

export function useAssignFreight(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AssignFreightData) =>
      api.patch<Freight>(`/freights/${id}/assign`, data).then((r) => r.data),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["freights"] });
      qc.setQueryData(KEYS.detail(id), updated);
    },
  });
}

export interface UpdateFreightFinanceData {
  freightValue?: number;
  driverValue?: number;
  advancePercent?: number;
  notes?: string;
}

export function useUpdateFreightFinance(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateFreightFinanceData) =>
      api.patch<Freight>(`/freights/${id}/finance`, data).then((r) => r.data),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["freights"] });
      qc.setQueryData(KEYS.detail(id), updated);
    },
  });
}

export function useTransitionFreight(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: "start" | "deliver" | "proof" | "complete") =>
      api.patch<Freight>(`/freights/${id}/${action}`).then((r) => r.data),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["freights"] });
      qc.setQueryData(KEYS.detail(id), updated);
    },
  });
}

export function useReceiveFreight(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ReceiveFreightData) =>
      api.post<Freight>(`/freights/${id}/receive`, data).then((r) => r.data),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["freights"] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["cash"] });
      qc.setQueryData(KEYS.detail(id), updated);
    },
  });
}
